/**
 * Fechas del contrato de grilla: el filtro de encabezado de una fecha es un árbol año › mes › día en
 * la zona del negocio (`America/Santiago`), no en UTC, igual que en la API (`$dateToParts`).
 */
import type { GridFacetValue } from './grid-query';
import { isEmpty, toInstant } from './compare';

export const GRID_TIMEZONE = 'America/Santiago';

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let format = formatters.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
    formatters.set(timeZone, format);
  }
  return format;
}

export interface DateParts {
  year: number;
  month: number;
  day: number;
}

/** Año, mes y día del instante en `timeZone`; `null` si está vacío o no es fecha. */
export function datePartsIn(value: unknown, timeZone = GRID_TIMEZONE): DateParts | null {
  if (isEmpty(value)) return null;
  const instant = toInstant(value);
  if (Number.isNaN(instant)) return null;
  const parts = formatter(timeZone).formatToParts(new Date(instant));
  const part = (type: string) => Number(parts.find((item) => item.type === type)!.value);
  return { year: part('year'), month: part('month'), day: part('day') };
}

/** Árbol del facet de fechas: vacíos primero, cada nivel en orden ascendente. */
export function buildDateTree(dates: (DateParts | null)[]): GridFacetValue[] {
  let empty = 0;
  const years = new Map<number, Map<number, Map<number, number>>>();
  for (const parts of dates) {
    if (!parts) {
      empty += 1;
      continue;
    }
    const months = years.get(parts.year) ?? new Map<number, Map<number, number>>();
    years.set(parts.year, months);
    const days = months.get(parts.month) ?? new Map<number, number>();
    months.set(parts.month, days);
    days.set(parts.day, (days.get(parts.day) ?? 0) + 1);
  }
  const ascending = <T>(entries: [number, T][]) => entries.sort(([a], [b]) => a - b);
  const sum = (nodes: GridFacetValue[]) => nodes.reduce((total, node) => total + node.count, 0);
  const tree = ascending([...years]).map(([year, months]) => {
    const monthNodes = ascending([...months]).map(([month, days]) => {
      const dayNodes = ascending([...days]).map(([day, count]) => ({ value: day, count }));
      return { value: month, count: sum(dayNodes), children: dayNodes };
    });
    return { value: year, count: sum(monthNodes), children: monthNodes };
  });
  return empty ? [{ value: null, count: empty }, ...tree] : tree;
}

/** ¿El instante `t` cae en `parts` o después (en `timeZone`)? */
function atOrAfter(t: number, parts: DateParts, timeZone: string): boolean {
  const local = datePartsIn(t, timeZone)!;
  if (local.year !== parts.year) return local.year > parts.year;
  if (local.month !== parts.month) return local.month > parts.month;
  return local.day >= parts.day;
}

/**
 * Primer instante del día `parts` en `timeZone`: su medianoche o, si ese día empieza con el cambio
 * de horario (en Chile, el de septiembre salta de 00:00 a 01:00), la primera hora que existe. Se
 * busca entre ±36 h del mediodía UTC: la fecha local avanza siempre con el instante.
 */
function startOfDay(parts: DateParts, timeZone: string): number {
  const noon = Date.UTC(parts.year, parts.month - 1, parts.day, 12);
  let low = noon - 36 * 3_600_000;
  let high = noon + 36 * 3_600_000;
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (atOrAfter(middle, parts, timeZone)) high = middle;
    else low = middle;
  }
  return high;
}

/**
 * Rango `[desde, hasta)` en ISO de un nodo del árbol de fechas (año, año y mes, o día) en
 * `timeZone`, para el filtro `range`: de la medianoche local de su comienzo a la del siguiente.
 * Un día puede durar 23 o 25 horas por el horario de verano.
 */
export function dayRange(
  node: { year: number; month?: number; day?: number },
  timeZone = GRID_TIMEZONE,
): [string, string] {
  const { year, month, day } = node;
  const from = { year, month: month ?? 1, day: day ?? 1 };
  const next = new Date(
    Date.UTC(
      year + (month ? 0 : 1),
      (month ?? 1) - 1 + (month && !day ? 1 : 0),
      (day ?? 1) + (day ? 1 : 0),
    ),
  );
  const to = { year: next.getUTCFullYear(), month: next.getUTCMonth() + 1, day: next.getUTCDate() };
  return [
    new Date(startOfDay(from, timeZone)).toISOString(),
    new Date(startOfDay(to, timeZone)).toISOString(),
  ];
}
